import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import {
  getTransportLogisticsGetUnhandledDeviationReportsQueryKey,
  useTransportLogisticsGetUnhandledDeviationReports,
} from 'api/transport-logistics/transport-logistics';
import useUsers from 'contexts/basicData/useUsers';
import { formatDateTime } from 'utils/date-helpers';
import Checkbox from 'components/inputs/Checkbox';
import CaseOpenLink from 'components/CaseOpenLink';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { useCaseMarkEventsAsReadByAdmin } from 'api/case/case';
import { useQueryClient } from '@tanstack/react-query';
import { CaseEventDTO } from 'api/model';
import {
  Centered,
  getTableRowClassName,
  SavingRowAnimationWrapper,
} from 'components/Dashboard/table-helpers';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import useBaseCaseEventWidgetColumnSettings from './useBaseCaseEventWidgetColumnSettings';

const TABLE_NAME = 'deviation-reports';

const DeviationReportsWidget = () => {
  const [deviationReports, setDeviationReports] = useState<CaseEventDTO[]>([]);
  const [ongoingSaves, setOngoingSaves] = useState<number[]>([]);
  const users = useUsers();
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();

  const queryClient = useQueryClient();
  const { mutateAsync: markEventAsRead, status: markAsReadStatus } =
    useCaseMarkEventsAsReadByAdmin({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries([
            `${getTransportLogisticsGetUnhandledDeviationReportsQueryKey()}`,
          ]);
        },
      },
    });

  const getReports = useTransportLogisticsGetUnhandledDeviationReports(
    {
      startDate: daterange[0] ?? undefined,
      endDate: daterange[1] ?? undefined,
      businessUnitIDs: selectedBusinessUnits,
    },
    {
      query: {
        refetchInterval: UPDATE_INTERVAL,
      },
    }
  );

  useEffect(() => {
    if (getReports.data) {
      setDeviationReports(getReports.data);
    } else {
      setDeviationReports([] as CaseEventDTO[]);
    }
  }, [getReports.data]);

  const filteredComments = useMemo(() => {
    if (!searchText.length) {
      return deviationReports;
    }
    return deviationReports
      ? deviationReports.filter((c) => {
          return (
            c.comment?.toLowerCase().includes(searchText.toLowerCase()) ||
            c.registrationNumber
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (users[c.createdByID]?.name ?? '')
              .toLowerCase()
              .includes(searchText.toLowerCase())
          );
        })
      : [];
  }, [deviationReports, searchText, users]);

  const baseColumnSettings = useBaseCaseEventWidgetColumnSettings();

  const columnSettings: ColumnSetting<CaseEventDTO, any>[] = useMemo(() => {
    const columns: ColumnSetting<CaseEventDTO, any>[] =
      baseColumnSettings.concat([
        {
          head: 'Tid',
          render: (row) => `${formatDateTime(row.created)}`,
          width: 100,
          sortFunction: (a, b) => a.created.getTime() - b.created.getTime(),
        },
        {
          head: 'Kommentar',
          render: (row) => row.comment,
          width: 300,
          sortFunction: (a, b) => a.comment.localeCompare(b.comment),
        },
        {
          head: ' ',
          render: (row) => (
            <Centered>
              <CaseOpenLink caseID={row.caseID} />
            </Centered>
          ),
          width: 20,
        },
        {
          head: 'Klart',
          render: (row) => (
            <Centered>
              <Checkbox
                disabled={markAsReadStatus === 'loading'}
                onChange={async () => {
                  setOngoingSaves([...ongoingSaves, row.id]);
                  await markEventAsRead({ data: [row.id] });
                  setOngoingSaves(ongoingSaves.filter((id) => id !== row.id));
                }}
              />
            </Centered>
          ),
          width: 30,
        },
      ]);
    return columns;
  }, [baseColumnSettings, markAsReadStatus, markEventAsRead, ongoingSaves]);
  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.DeviationReports]}
      count={deviationReports.length}
      isLoading={getReports.isLoading}
      info="Markera som klar för att markera kommentar som läst."
    >
      <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      >
        <Table
          columnSettings={columnSettings}
          rows={filteredComments}
          rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
          useColumnWidthAsFlexAmount
        />
      </SavingRowAnimationWrapper>
    </Widget>
  );
};

export default DeviationReportsWidget;
